import React, { useContext, useState, useRef, useEffect } from 'react'
import './NavBar.scss'
import { Burger, CreateAccountModal, SignInModal, ResetPasswordModal, SmallModal } from 'sub-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import AuthContext from 'app/AuthContext';
import profileImage from 'images/blank-profile-picture.png'
import { getProjCategoryOpts } from 'lib/project';
import { getEnvConfig, formatAvatarUrl } from 'lib/utils';
import AlertCircle from "@untitled-ui/icons-react/build/cjs/AlertCircle";
import CameraPlus from "@untitled-ui/icons-react/build/cjs/CameraPlus";

const NavBar = ({ isSignInToggleOnPrompt, redirectURL }) => {
	const [areYouSureModal, setAreYouSureModal] = useState(false);
	const [isSignUpToggleOn, setIsSignUpToggleOn] = useState(false);
	const [isSignInToggleOn, setIsSignInToggleOn] = useState(false);
	const [isResetPwdToggleOn, setResetPwdToggleOn] = useState(false);
	const [isCreateToggleOn, setIsCreateToggleOn] = useState(false);
	const [actionCompleted, setActionCompleted] = useState(false);
	const [timeoutId, setTimeoutId] = useState(null);
	const [actionCompletedType, setActionCompletedType] = useState(null);
	const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 600);

	const { authToken, userMetadata, logout } = useContext(AuthContext);
	const userId = userMetadata ? userMetadata.id : "";
	const previousAuthToken = useRef(authToken);
	const navigate = useNavigate();

	// Manages the profile access in the nav bar when a user is logged in
	const profileMenuRef = useRef();
	const profileCircleRef = useRef();
	const [profileMenuVisible, setProfileMenuVisible] = useState(false);
	const [profileMenuClosing, setProfileMenuClosing] = useState(false);
	const category = getProjCategoryOpts();

	useEffect(() => {
		if (isSignInToggleOnPrompt && isSignInToggleOnPrompt === true) {
			setIsSignInToggleOn(!isSignInToggleOn);
			setActionCompleted(false);
		}
	}, [isSignInToggleOnPrompt]);

	// Navigates the user to the home page when their token expires
	useEffect(() => {
		if (previousAuthToken.current && authToken === null) {
			navigate('/');
		}
		previousAuthToken.current = authToken;
	}, [authToken, navigate]);

	// Closes the profile menu when the user clicks outside of it
	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Fixes some glitches with the profile and its menu
	useEffect(() => {
		const handleProfileCircleClick = () => {
			setProfileMenuVisible((prevState) => {
				const newState = !prevState;
				if (process.env.REACT_APP_ENVIRONMENT === "dev") {
					console.log("New profile menu state:", newState);
				}
				return newState;
			});
		};

		if (profileCircleRef.current) {
			profileCircleRef.current.addEventListener("click", handleProfileCircleClick);
		}

		return () => {
			if (profileCircleRef.current) {
				profileCircleRef.current.removeEventListener("click", handleProfileCircleClick);
			}
		};
	}, [authToken]);

	const location = useLocation();

	useEffect(() => {
		function handleScroll() {
			const topMenu = document.querySelector('.top-menu');
			if (window.scrollY > 0) {
				topMenu.style.backgroundColor = 'rgba(41, 49, 60, 0.9)'; // Change to your desired color
			} else {
				topMenu.style.backgroundColor = 'transparent';
			}
		}

		function checkPathAndAddScrollListener() {
			const currentPath = location.pathname;
			if (currentPath === '/') {
				window.addEventListener('scroll', handleScroll);
				// Call handleScroll initially to set the correct background on page load
				handleScroll();
			}
			else {
				window.removeEventListener('scroll', handleScroll);
				// Optionally, reset the background color if navigating away from EarlyLandingPage
				const topMenu = document.querySelector('.top-menu');
				if (topMenu) {
					if (isSmallScreen && !currentPath.includes('onboarding') && !currentPath.includes('/edit')) {
						topMenu.style.backgroundColor = 'transparent'; // Set to default solid color or any desired color
					}
					else {
						topMenu.style.backgroundColor = 'rgba(41, 49, 60, 0.9)'; // Set to default solid color or any desired color
					}
				}
			}
		}

		checkPathAndAddScrollListener();

		// Cleanup scroll event listener on component unmount or path change
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [location.pathname]);


	const handleActionCompleted = (actionType) => {
		if (actionType) { setActionCompletedType(actionType); }
		setActionCompleted(true);
		if (actionType == "signup_user") {
			navigate('/discovery');
		}
		// This is for timing the prompt message, then auto closing it after 1000ms/1 sec
		// const id = setTimeout(() => {
		//     setIsSignUpToggleOn(false);
		// }, 1000);
		// setTimeoutId(id);
	};

	const handleSignInCompleted = () => {
		setActionCompleted(true);
		navigate('/discovery');
	}

	const resetPwdCompleted = () => {
		setActionCompleted(true);
	}

	const handleCreateCompleted = () => {
		setActionCompleted(true);
		const id = setTimeout(() => {
			setIsCreateToggleOn(false);
			window.location.reload();
		}, 1000);
		setTimeoutId(id);
		if (window.location.pathname.includes("/profile")) {
			navigate('/profile/' + userId);
		} else {
			navigate(window.location.pathname);
		}
	}

	const isOnboardingCompleted = () => {
		const isOnboarding = location.pathname.includes('onboarding');
		if (isOnboarding && userMetadata && (userMetadata.last_successful_login_ts === null || userMetadata.last_successful_login_ts < 1744948800000)) {
			setAreYouSureModal(true);
			return false;
		}
		return true;
	}

	const handleProfileClick = () => {
		if (isOnboardingCompleted()) {
			navigate(`/profile/${userId}?is_profile_view=true`);
		}
		setProfileMenuClosing(true);
		setTimeout(() => {
			setProfileMenuVisible(false);
			setProfileMenuClosing(false);
		}, 300);
	};

	const handleAccountPrefClick = () => {
		if (isSignInToggleOn) {
			setIsSignInToggleOn(false);
			setIsSignUpToggleOn(false);
		}
		if (isSignUpToggleOn) {
			setIsSignInToggleOn(false);
			setIsSignUpToggleOn(false);
		}
		navigate(`/profile/${userId}/settings`);
		setProfileMenuClosing(true);
		setTimeout(() => {
			setProfileMenuVisible(false);
			setProfileMenuClosing(false);
		}, 300);
	};

	const handleCreateClick = () => {
		if (isOnboardingCompleted()) {
			if (isSignInToggleOn) {
				setIsSignInToggleOn(false);
				setIsSignUpToggleOn(false);
			}
			if (isSignUpToggleOn) {
				setIsSignInToggleOn(false);
				setIsSignUpToggleOn(false);
			}
			// setIsCreateToggleOn(!isCreateToggleOn);
			setActionCompleted(false);
			setProfileMenuVisible(false);
			navigate(`/profile/${userId}/video/edit`);
		}
	}

	const handleSignUpClick = () => {
		setIsSignUpToggleOn(!isSignUpToggleOn);
		setActionCompletedType(null);
		setActionCompleted(false);
	};

	const handleSignInClick = () => {
		setIsSignInToggleOn(!isSignInToggleOn);
		setActionCompleted(false);
	};

	const resetPwdClick = () => {
		setResetPwdToggleOn(!isResetPwdToggleOn);
		setActionCompleted(false);
	};

	const handleLogOutClick = () => {
		setProfileMenuClosing(true);
		setTimeout(() => {
			setProfileMenuVisible(false);
			setProfileMenuClosing(false);
			navigate('/');
			logout();
		}, 300);
	}

	const handleClickOutside = (event) => {
		if (
			profileMenuRef.current &&
			!profileMenuRef.current.contains(event.target) &&
			!profileCircleRef.current.contains(event.target)
		) {
			setProfileMenuVisible(false);
		}
	};

	const handleCategoryClick = (category) => {
		navigate(`/discovery?category=${category}`);
	}

	const handleCommunityClick = (comm) => {
		navigate(`${comm}`);
	}

	const handleBrowseClick = () => {
		if (isSignInToggleOn || isSignUpToggleOn) {
			setIsSignInToggleOn(false);
			setIsSignUpToggleOn(false);
		}
		setProfileMenuVisible(false);
		navigate(`/discovery`);
	}

	const handleLogoClick = () => {
		if (isOnboardingCompleted()) {
			authToken ? navigate(`/discovery`) : navigate('/');
		}
	}

	/**
	 * The function `handleSignedIn` checks if there is an authentication token and a redirect URL, and if
	 * so, it navigates to the specified URL; otherwise, it clears a timeout.
	 */
	const handleSignedIn = () => {
		if ((authToken) && (redirectURL && redirectURL !== '')) {
			const url = new URL(redirectURL);
			navigate(url.pathname + url.search);
		} else {
			clearTimeout(timeoutId);
		}
	}

	// Get messaging info from the config files
	const messaging = getEnvConfig().messaging;

	return (
		<nav className="colorlib-nav" role="navigation" style={{ flex: 1, flexWrap: 'wrap' }}>
			<Burger href="javascript:void(0)" handleSignInClick={handleSignInClick} handleSignUpClick={handleSignUpClick} handleLogOutClick={handleLogOutClick} handleProfileClick={handleProfileClick} handleCreateClick={handleCreateClick} resetPwdClick={resetPwdClick} setAreYouSureModal={setAreYouSureModal} />
			{isSignUpToggleOn &&
				<CreateAccountModal isSignUpToggleOn={isSignUpToggleOn} handleClick={handleSignUpClick} handleSignInClick={handleSignInClick} />
			}
			{isSignInToggleOn &&
				<SignInModal resetPwdClick={resetPwdClick} isSignInToggleOn={isSignInToggleOn} handleClick={handleSignInClick} onActionCompleted={handleSignInCompleted} handleSignUpClick={handleSignUpClick} />
			}
			<ResetPasswordModal isResetPwdToggleOn={isResetPwdToggleOn} handleClick={resetPwdClick} onActionCompleted={resetPwdCompleted} />
			{areYouSureModal && (
				<SmallModal
					setDisplayModal={setAreYouSureModal}
					display={areYouSureModal}
					hideCloseBtn={true}
				// onCloseAction={() => navigate(`/profile/${userId}?is_profile_view=true`)}
				>
					<div className={'successMessage'}>
						<AlertCircle className={'icon'} />
						<h3>Can't stand another form?</h3>
						<p style={{ textAlign: 'center' }}>If you fill out the next page,<br /> I promise I'll let you in. 🙂</p>
					</div>
				</SmallModal>
			)}
			<div className="top-menu">
				<div className="left-nav-buttons col-md-4 text-left">
					<div className="main-logo" onClick={handleLogoClick}>
						sidejawn
						<div className='sub-tag'>
							beta
						</div>
					</div>
					<ul className='disappear-on-resize'>
						{/* <li><Link to="/about">About</Link></li> */}
						{/* <li><Link to="/resources">Resources</Link></li> */}
						{/* <li className="has-dropdown">
								<a style={{cursor:'pointer'}} onClick={() => handleBrowseClick()}>Startups </a>
								<ul id="dropdown" className='organized'>
									<li><Link to="/discovery">All Startups</Link></li>
									{category.map(item => (
										<li key={item.value}>
											<a style={{cursor:'pointer'}} onClick={() => handleCategoryClick(item.value)}>{item.label}</a>
										</li>
									))}
								</ul>
							</li> */}
						{/* <li className="has-dropdown">
								<a href="#">Communities </a>
								<ul id="dropdown">
									<li><a style={{cursor:'pointer'}} onClick={() => handleCommunityClick('/venture-cafe')}>Venture Cafe</a></li>
									<li><a style={{cursor:'pointer'}} onClick={() => handleCommunityClick('/founder-institute')}>PSL & Founder Institute</a></li>
								</ul>
							</li> */}
					</ul>
				</div>
			</div>
		</nav>
	)
}

export default NavBar;